import React from "react";
import SidebarDraggableField from "./SidebarDraggableField";
import { FieldData } from "./DraggableResizableField";

const signFields = ["Signature", "Tampon", "Text", "Date"];

interface SignatureSidebarProps {
  editorMode: string;
  handleEditorMode: (mode: string) => void;
  handleDownload?: () => void;
  onSign: () => void;
  handleRemoveTextField?: (index: number) => void;
  setIsModalOpen?: (open: boolean) => void;
  onEdit: () => void;
  customTextFields?: string[];
  loadingData: boolean;
  onClose: () => void;
  fields: FieldData[];
  onlyEdit?: boolean;
  signButtonLabel?: string;
  editButtonLabel?: string;
}

function SignatureSidebar({
  editorMode,
  handleEditorMode,
  handleDownload,
  onSign,
  handleRemoveTextField,
  setIsModalOpen,
  onEdit,
  customTextFields = [],
  loadingData,
  onClose,
  fields,
  onlyEdit = false,
  signButtonLabel = "Prêt pour signature",
  editButtonLabel = "Enregistrer les modifications",
}: SignatureSidebarProps) {
  const handlePrepareSignature = () => {
    handleEditorMode("sign");
  };

  const handleEditPDF = () => {
    handleEditorMode("edit");
  };

  return (
    <div className="w-96 p-6 h-full overflow-auto bg-white border-r border-gray-200">
      {editorMode === "choice" && (
        <div className="flex flex-col gap-3">
          <button
            onClick={handlePrepareSignature}
            className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 text-base"
          >
            Préparer une signature
          </button>
          <button
            onClick={handleEditPDF}
            className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
          >
            Modifier le PDF
          </button>
          {handleDownload && (
            <button
              onClick={handleDownload}
              className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
            >
              Télécharger
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
          >
            Fermer
          </button>
        </div>
      )}

      {editorMode === "sign" && (
        <div className="flex flex-col gap-3 h-full">
          <div className="text-lg font-semibold">Champs</div>
          <div className="text-sm text-gray-600 mb-2">
            Glissez-déposez les champs sur le PDF pour les positionner. Vous pouvez déplacer, redimensionner et modifier les champs texte.
          </div>
          {signFields.map((field) => (
            <SidebarDraggableField key={field} field={field} type={field} />
          ))}
          {customTextFields.map((fieldName, index) => (
            <div
              key={index}
              className="flex flex-row justify-between items-center w-full gap-2"
            >
              <SidebarDraggableField field={fieldName} type="text" />
              {handleRemoveTextField && (
                <div
                  className="flex items-center cursor-pointer border border-gray-300 rounded-full p-1 hover:bg-red-400 hover:border-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTextField(index);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="gray"
                    width="18"
                    height="18"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </div>
              )}
            </div>
          ))}
          {setIsModalOpen && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 flex items-center justify-center gap-2 text-base"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="gray"
                width="18"
                height="18"
              >
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
              <span>Nouveau champ</span>
            </button>
          )}
          <div className="flex flex-grow w-full gap-3 flex-col justify-end">
            <button
              className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
              onClick={() => handleEditorMode("choice")}
            >
              Annuler
            </button>
            <button
              className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
              onClick={onSign}
              disabled={loadingData}
            >
              {loadingData ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Traitement...
                </>
              ) : (
                signButtonLabel
              )}
            </button>
          </div>
        </div>
      )}

      {editorMode === "edit" && (
        <div className="flex flex-col gap-4 h-full">
          <button
            className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
            onClick={() => {
              onlyEdit ? onClose() : handleEditorMode("choice");
            }}
          >
            Annuler
          </button>
          <div className="text-sm text-gray-600">
            Déposez un champ texte sur le PDF pour le modifier. Une fois déposé, vous pouvez le déplacer, le redimensionner et modifier son contenu.
          </div>
          <div className="w-full">
            <SidebarDraggableField field="Champ Texte" type="Text" />
          </div>
          <div className="flex flex-col gap-3">
            <button
              className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
              onClick={onEdit}
              disabled={fields.length === 0 || loadingData}
            >
              {loadingData ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enregistrement...
                </div>
              ) : (
                editButtonLabel
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignatureSidebar;
